import { Service, signal } from '@angular/core';
import { ToastSize, ToastStructure, ToastVariant } from '@design-system';

/**
 * Matches the 3s the shared mobile toast used (`NotifyComponent.showNotifTime`), so migrating a
 * call site off `NotificationService` does not silently change how long the message stays up.
 */
const DEFAULT_DURATION_MS = 3000;

/** What a caller asks for. Everything but `message` falls back to the Figma defaults. */
export type ToastRequest = {
	readonly message: string;
	readonly variant?: ToastVariant;
	readonly structure?: ToastStructure;
	readonly size?: ToastSize;
	readonly actionLabel?: string;
	readonly durationMs?: number;
	/** Invoked when the toast's action control is activated. */
	readonly onAction?: () => void;
};

/** One toast currently on screen. */
export type ActiveToast = {
	readonly id: number;
	readonly message: string;
	/**
	 * How many times this message has been raised while it stayed on screen. 1 for a first
	 * showing; the host only renders the number once it goes above that.
	 */
	readonly count: number;
	readonly variant: ToastVariant;
	readonly structure: ToastStructure;
	readonly size: ToastSize;
	readonly actionLabel: string;
	readonly onAction?: () => void;
};

/**
 * Toast queue for kala-mobile, rendered by `ToastHostComponent`.
 *
 * This is the single way kala raises a toast; `@shared`'s `NotificationService` is not used. The
 * queue lives in the app rather than beside `ui-toast` because a signal-based queue is app state,
 * and `scope:design-system` is boundary-restricted to asset, icon, tooling and shared-type libs.
 */
@Service()
export class ToastService {
	private readonly items = signal<readonly ActiveToast[]>([]);
	private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();
	private nextId = 0;

	/** Newest first, so the host renders the most recent toast at the top of the stack. */
	readonly toasts = this.items.asReadonly();

	/**
	 * Queues a toast and returns its id. Repeating a message that is already on screen bumps that
	 * toast's `count` and restarts its timer rather than stacking a second copy — the same
	 * de-duplication the shared toast did via `showCount`, which rendered the tally beside the text.
	 *
	 * Both halves matter: the repeat is usually a repeated action — a second submit rejected for the
	 * same reason — so without the restart the toast could expire on the first submit's clock, and
	 * without the count an unchanged banner looks like nothing happened the second time.
	 */
	show(request: ToastRequest): number {
		const duration = request.durationMs ?? DEFAULT_DURATION_MS;
		const existing = this.items().find(item => item.message === request.message);
		if (existing) {
			this.items.update(list =>
				list.map(item => (item.id === existing.id ? { ...item, count: item.count + 1 } : item))
			);
			this.startTimer(existing.id, duration);
			return existing.id;
		}

		const id = this.nextId++;
		const toast: ActiveToast = {
			id,
			message: request.message,
			count: 1,
			variant: request.variant ?? 'blue',
			structure: request.structure ?? (request.onAction ? 'button' : 'icon'),
			size: request.size ?? 'lg',
			actionLabel: request.actionLabel ?? 'مشاهده',
			onAction: request.onAction
		};

		this.items.update(list => [toast, ...list]);
		this.startTimer(id, duration);

		return id;
	}

	success(message: string, request?: Omit<ToastRequest, 'message' | 'variant'>): number {
		return this.show({ ...request, message, variant: 'green' });
	}

	error(message: string, request?: Omit<ToastRequest, 'message' | 'variant'>): number {
		return this.show({ ...request, message, variant: 'red' });
	}

	warning(message: string, request?: Omit<ToastRequest, 'message' | 'variant'>): number {
		return this.show({ ...request, message, variant: 'yellow' });
	}

	info(message: string, request?: Omit<ToastRequest, 'message' | 'variant'>): number {
		return this.show({ ...request, message, variant: 'blue' });
	}

	dismiss(id: number): void {
		this.clearTimer(id);
		this.items.update(list => list.filter(item => item.id !== id));
	}

	/** Replaces any pending dismissal, so a restarted toast gets the full duration again. */
	private startTimer(id: number, durationMs: number): void {
		this.clearTimer(id);
		this.timers.set(
			id,
			setTimeout(() => this.dismiss(id), durationMs)
		);
	}

	private clearTimer(id: number): void {
		const timer = this.timers.get(id);
		if (timer !== undefined) {
			clearTimeout(timer);
			this.timers.delete(id);
		}
	}

	clear(): void {
		this.timers.forEach(timer => clearTimeout(timer));
		this.timers.clear();
		this.items.set([]);
	}
}
